import {
    ChatBubbleLeftRightIcon,
    DevicePhoneMobileIcon,
    EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FaFacebookMessenger, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import type { ComponentType, SVGProps } from 'react';

import { t } from '../i18n/langue';

/**
 * La correspondance `action_type_id` → canal, seul endroit du mobile qui la connaît.
 *
 * Les identifiants sont ceux de `helpers/actionTypes.ts` (extraits de
 * `pages/flows/api/useActionType.ts` par #74) pour l'e-mail, le SMS et WhatsApp ;
 * le live chat, Messenger et Instagram n'y figurent pas — ce sont des canaux
 * entrants, pas des types d'action de flow — et sont déclarés ici.
 *
 * **Les couleurs sont des jetons de marque, jamais des couleurs Tailwind par
 * défaut.** La maquette de #16 habillait ses pastilles en `bg-blue-50
 * text-blue-700`, `bg-gray-100 text-gray-600` et consorts : `mobile:check`
 * l'interdit (#34 a mesuré `text-gray-400` à 2,54:1 sur blanc, quand AA en
 * demande 4,5). Ré-exprimer plutôt que recopier était le but de la frontière.
 *
 * La pastille ne porte **que** de la couleur et une icône, jamais du texte : elle
 * se pose sur un avatar de 44 px, et #16 a verrouillé l'extrait sur une seule
 * ligne — il n'y a pas de largeur pour un libellé. Le `libelle` sert au nom
 * accessible, et aux onglets de canal de #84.
 *
 * Le libellé est un accesseur : `CANAUX` est évalué au chargement du module, et
 * une chaîne posée là resterait dans la langue du démarrage. Les noms de marque
 * (WhatsApp, Messenger, Instagram) ne se traduisent pas.
 */

export const ACTION_TYPE_EMAIL = 1;
export const ACTION_TYPE_SMS = 2;
export const ACTION_TYPE_WHATSAPP = 3;
export const ACTION_TYPE_LIVE_CHAT = 14;
export const ACTION_TYPE_MESSENGER = 20;
export const ACTION_TYPE_INSTAGRAM = 21;

export type CleCanal =
    | 'email'
    | 'sms'
    | 'whatsapp'
    | 'live_chat'
    | 'messenger'
    | 'instagram'
    | 'autre';

export interface Canal {
    cle: CleCanal;
    /** Nom accessible de la pastille, et libellé des onglets de canal (#84). */
    libelle: string;
    /** Fond de la pastille. Jeton de marque uniquement. */
    fond: string;
    Icone: ComponentType<SVGProps<SVGSVGElement>>;
}

function canal(cle: CleCanal, libelle: () => string, fond: string, Icone: Canal['Icone']): Canal {
    return {
        cle,
        get libelle() {
            return libelle();
        },
        fond,
        Icone,
    };
}

const CANAUX: Record<CleCanal, Canal> = {
    email: canal('email', () => t('canaux.email'), 'bg-action-email-color', EnvelopeIcon),
    sms: canal('sms', () => t('canaux.sms'), 'bg-action-sms-color', DevicePhoneMobileIcon),
    whatsapp: canal('whatsapp', () => 'WhatsApp', 'bg-trigger-color', FaWhatsapp),
    live_chat: canal('live_chat', () => t('canaux.liveChat'), 'bg-main-color', ChatBubbleLeftRightIcon),
    messenger: canal('messenger', () => 'Messenger', 'bg-action-email-color', FaFacebookMessenger),
    instagram: canal('instagram', () => 'Instagram', 'bg-condition-color', FaInstagram),
    autre: canal('autre', () => t('canaux.autre'), 'bg-inactive-color', ChatBubbleLeftRightIcon),
};

const PAR_ACTION: Record<number, CleCanal> = {
    [ACTION_TYPE_EMAIL]: 'email',
    [ACTION_TYPE_SMS]: 'sms',
    [ACTION_TYPE_WHATSAPP]: 'whatsapp',
    [ACTION_TYPE_LIVE_CHAT]: 'live_chat',
    [ACTION_TYPE_MESSENGER]: 'messenger',
    [ACTION_TYPE_INSTAGRAM]: 'instagram',
};

/** Un identifiant inconnu ou absent rend « autre » — jamais `undefined`. */
export function canalDe(actionTypeId?: number | null): Canal {
    return CANAUX[(actionTypeId != null && PAR_ACTION[actionTypeId]) || 'autre'];
}
